'use client';

import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader2, X, ChevronDown, AlertCircle } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useRouter } from 'next/navigation';
import { useCartContext } from '@/components/CartProvider';
import RegionMismatchModal from '@/components/RegionMismatchModal';

interface PlayerVerifyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (verifiedUid: string, nickname: string, zoneId?: string) => void;
  game?: string;
  product?: any;
  hasZoneInput?: boolean;
  zoneLabel?: string;
  playerLabel?: string;
  gameImage?: string;
}

const PlayerVerifyModal = ({
  isOpen,
  onClose,
  onSuccess,
  game,
  product,
  gameImage,
  hasZoneInput = false,
  zoneLabel = 'Zone ID',
  playerLabel = 'Player ID',
}: PlayerVerifyModalProps) => {
  const router = useRouter();
  const {
    uid: globalUid,
    setUid: setGlobalUid,
    validationResult: globalValRes,
    setValidationResult: setGlobalValRes,
    selectedTabRegion,
    setSelectedTabRegion,
    setPurchasedLevels,
    setChannelLevelUps,
  } = useCartContext();

  const [localUid, setLocalUid] = useState(globalUid || '');
  const [localZoneId, setLocalZoneId] = useState(
    globalValRes?.zoneId || globalValRes?.zone_id || '',
  );
  const [isValidating, setIsValidating] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [tempValResult, setTempValResult] = useState<any>(null);
  const [showIdMismatchModal, setShowIdMismatchModal] = useState(false);
  const [mismatchData, setMismatchData] = useState<{
    detectedRegionCode: string;
    detectedRegionName: string;
    currentRegionCode: string;
    currentRegionName: string;
    targetUrl: string;
    actionButtonText: string;
  } | null>(null);
  const [savedPlayers, setSavedPlayers] = useState<any[]>([]);
  const [showSavedDropdown, setShowSavedDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load saved players from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('topupbot_saved_players');
    if (saved) {
      try {
        setSavedPlayers(JSON.parse(saved));
      } catch (e) {}
    }
  }, []);

  // Click-outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowSavedDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resolvedGame = (product?.game || game || 'Free Fire').trim();
  const normalizedGame = resolvedGame.toLowerCase();
  const isFreeFire = !resolvedGame || /free\s*fire|freefire/i.test(normalizedGame);
  const targetRegion = (
    product?.defaultRegion ||
    (selectedTabRegion === 'ID' || normalizedGame.includes('indonesia') ? 'ID' : 'MY')
  ).toString().trim().toUpperCase();

  const isSameCluster = (r1: string, r2: string) => {
    let c1 = (r1 || '').toUpperCase();
    let c2 = (r2 || '').toUpperCase();
    if (!c1 || !c2) return false;
    if (c1 === 'LK' || c1 === 'SRI LANKA' || c1 === 'SRILANKA' || c1.includes('LANKA')) c1 = 'SG';
    if (c2 === 'LK' || c2 === 'SRI LANKA' || c2 === 'SRILANKA' || c2.includes('LANKA')) c2 = 'SG';
    if (c1 === c2) return true;
    const sgCluster = ['SG', 'MY', 'LK', 'SRI LANKA', 'SRILANKA', 'MY-SG', 'MY/SG', 'SG/MY'];
    return sgCluster.includes(c1) && sgCluster.includes(c2);
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const hasRequiredZone =
        !hasZoneInput || Boolean(globalValRes?.zoneId || globalValRes?.zone_id);

      const regionMatches =
        !targetRegion ||
        !globalValRes?.region ||
        isSameCluster(targetRegion, globalValRes.region);

      const gameMatches = !globalValRes?.game || (
        isFreeFire
          ? /free\s*fire|freefire/i.test(globalValRes.game)
          : globalValRes.game.toLowerCase() === normalizedGame
      );

      if ((globalValRes?.valid || globalValRes?.nickname) && hasRequiredZone && regionMatches && gameMatches) {
        setTempValResult(globalValRes);
        setLocalUid(globalUid || '');
        setLocalZoneId(globalValRes?.zoneId || globalValRes?.zone_id || '');
      } else {
        setLocalUid(globalUid || '');
        setLocalZoneId(globalValRes?.zoneId || globalValRes?.zone_id || '');
        setTempValResult(null);
        setErrorMsg(null);
      }
    }
  }, [isOpen, globalUid, globalValRes, hasZoneInput, targetRegion, normalizedGame, isFreeFire]);

  const relevantSavedPlayers = savedPlayers.filter((p) => {
    if (!p.game) return isFreeFire;
    const pGame = p.game.toString().trim().toLowerCase();
    if (isFreeFire) {
      return /free\s*fire|freefire/i.test(pGame);
    }
    return pGame === normalizedGame || normalizedGame.includes(pGame) || pGame.includes(normalizedGame);
  });

  if (!isOpen) return null;

  const handleValidate = async (targetUid?: string, targetZoneId?: string) => {
    const uidToValidate = typeof targetUid === 'string' ? targetUid : localUid;
    const zoneToValidate =
      typeof targetZoneId === 'string' ? targetZoneId : localZoneId;

    if (!uidToValidate.trim()) {
      setErrorMsg(`Please enter your ${playerLabel}`);
      return;
    }
    if (hasZoneInput && !zoneToValidate.trim()) {
      setErrorMsg(`Please enter your ${zoneLabel}`);
      return;
    }

    if (typeof targetUid === 'string') setLocalUid(targetUid);
    if (typeof targetZoneId === 'string') setLocalZoneId(targetZoneId);
    setIsValidating(true);
    setErrorMsg(null);
    setTempValResult(null);

    const API_BASE = '/api';
    try {
      const resp = await axios.post(`${API_BASE}/public/validate-player`, {
        playerId: uidToValidate.trim(),
        zoneId: hasZoneInput ? zoneToValidate.trim() : undefined,
        region: product?.defaultRegion || targetRegion,
        game: isFreeFire ? undefined : resolvedGame,
        productCode: product?.gamerhubProductCode || undefined,
        productId: product?.id || undefined,
      });
      const result = resp.data?.data || resp.data;
      const validationData = result.data
        ? { ...result.data, valid: result.valid }
        : result;

      const playerRegion =
        validationData?.region || targetRegion || 'MY';

      let purchasedLevelsData = {};
      let channelLevelUpsData: any[] = [];
      if (isFreeFire && playerRegion !== 'ID') {
        try {
          const shopRes = await axios.get(
            `${API_BASE}/garena/shop/channels?uid=${uidToValidate.trim()}&region=${playerRegion}`,
          );
          const shopData = shopRes.data?.data || shopRes.data;
          purchasedLevelsData = shopData.purchasedLevels || {};
          channelLevelUpsData = shopData.eligiblePacks || [];
        } catch (e) {
          console.error('Failed to load shop channels in modal:', e);
        }
      }

      if (validationData.valid || validationData.nickname) {
        if (hasZoneInput && zoneToValidate.trim()) {
          validationData.zoneId = zoneToValidate.trim();
          validationData.zone_id = zoneToValidate.trim();
        }
        validationData.game = isFreeFire ? 'Free Fire' : resolvedGame;
        if (!validationData.region && targetRegion) {
          validationData.region = targetRegion;
        }

        setTempValResult(validationData);

        // Persist to global context
        setGlobalUid(uidToValidate.trim());
        setGlobalValRes(validationData);
        const detectedRegion = (validationData.region || 'SG').toUpperCase();
        setSelectedTabRegion(detectedRegion === 'ID' ? 'ID' : 'SG');
        setPurchasedLevels(purchasedLevelsData);
        setChannelLevelUps(channelLevelUpsData);

        // Save to recent players list
        const newPlayer = {
          uid: uidToValidate.trim(),
          zoneId: hasZoneInput ? zoneToValidate.trim() : undefined,
          nickname: validationData.nickname || 'Unknown Player',
          game: isFreeFire ? 'Free Fire' : resolvedGame,
          region: validationData.region || targetRegion || 'MY',
          avatar: gameImage || '/assets/avatar-testimonial.png',
        };
        const updated = [
          newPlayer,
          ...savedPlayers.filter((p) => p.uid !== uidToValidate.trim()),
        ].slice(0, 10);
        setSavedPlayers(updated);
        localStorage.setItem('topupbot_saved_players', JSON.stringify(updated));

        // Confetti celebration
        confetti({
          particleCount: 50,
          spread: 60,
          origin: { y: 0.6 },
          colors: ['#FF4D4D', '#4DA6FF', '#4DFF88', '#FFE44D', '#BB66FF'],
          zIndex: 999999,
        });

        // Trigger success callback after a short delay so the user sees the animation
        if (onSuccess) {
          setTimeout(() => {
            onSuccess(
              uidToValidate.trim(),
              validationData.nickname || 'Player',
              hasZoneInput ? zoneToValidate.trim() : undefined,
            );
          }, 1200);
        }
      } else {
        const isMismatch =
          validationData?.error === 'REGION_MISMATCH' ||
          validationData?.reason === 'region_mismatch';
        if (isMismatch) {
          let detectedRegion = (
            validationData?.playerRegion ||
            validationData?.region ||
            ''
          ).toUpperCase();
          if (
            detectedRegion === 'LK' ||
            detectedRegion === 'SRI LANKA' ||
            detectedRegion === 'SRILANKA' ||
            detectedRegion.includes('LANKA')
          ) {
            detectedRegion = 'SG';
          }
          const currentRegionCode = targetRegion || 'SG';
          if (detectedRegion && !isSameCluster(detectedRegion, currentRegionCode)) {
            const regionNames: Record<string, string> = {
              ID: 'Indonesia (ID)',
              SG: 'Singapore / Malaysia (SG/MY)',
              MY: 'Singapore / Malaysia (SG/MY)',
              LK: 'Singapore / Malaysia (SG/MY)',
              TW: 'Taiwan (TW)',
              VN: 'Vietnam (VN)',
              TH: 'Thailand (TH)',
              PH: 'Philippines (PH)',
              LATAM: 'Latin America (LATAM)',
              BR: 'Brazil (BR)',
              GLOBAL: 'Global',
              BD: 'Bangladesh (BD)',
            };

            const regionSlugMap: Record<string, string> = {
              ID: 'indonesia',
              SG: 'my-sg',
              MY: 'my-sg',
              LK: 'my-sg',
              TW: 'tw',
              VN: 'vn',
              TH: 'th',
              PH: 'ph',
              LATAM: 'latam',
              BR: 'br',
              GLOBAL: 'global',
              BD: 'bd',
            };

            const detectedName = regionNames[detectedRegion] || detectedRegion;
            const currentName = regionNames[currentRegionCode] || currentRegionCode;
            const targetSlug = regionSlugMap[detectedRegion] || detectedRegion.toLowerCase();

            const targetUrl = isFreeFire
              ? `/games/free-fire?region=${targetSlug}&prefill=${encodeURIComponent(uidToValidate.trim())}#player-verify`
              : product?.catalogGame?.slug
              ? `/games/${product.catalogGame.slug}?region=${targetSlug}`
              : `/games/${resolvedGame.toLowerCase()}?region=${targetSlug}`;

            const actionButtonText = isFreeFire
              ? `Switch to Free Fire ${detectedName} Top Up`
              : `Switch to ${resolvedGame} ${detectedName} Top Up`;

            setMismatchData({
              detectedRegionCode: detectedRegion,
              detectedRegionName: detectedName,
              currentRegionCode: currentRegionCode,
              currentRegionName: currentName,
              targetUrl,
              actionButtonText,
            });
            setShowIdMismatchModal(true);
          }
        }

        if (validationData.reason === 'region_mismatch' && validationData.redirect_slug) {
          setErrorMsg(`Region incorrect. This player ID is from ${validationData.region}. Switching to the ${validationData.region} top-up page...`);
          setTimeout(() => {
            onClose();
            router.push(`/products/${validationData.redirect_slug}`);
          }, 1200);
          return;
        }
        setErrorMsg(
          validationData.message ||
            (hasZoneInput
              ? `Invalid ${playerLabel} or ${zoneLabel}`
              : `Invalid ${playerLabel}`),
        );
      }
    } catch (e: any) {
      console.error(e);
      const errData = e.response?.data?.data || e.response?.data;
      const isMismatch =
        errData?.error === 'REGION_MISMATCH' ||
        errData?.reason === 'region_mismatch';
      if (isMismatch) {
        let detectedRegion = (
          errData?.playerRegion ||
          errData?.region ||
          ''
        ).toUpperCase();
        if (
          detectedRegion === 'LK' ||
          detectedRegion === 'SRI LANKA' ||
          detectedRegion === 'SRILANKA' ||
          detectedRegion.includes('LANKA')
        ) {
          detectedRegion = 'SG';
        }
        const currentRegionCode = targetRegion || 'SG';
        if (detectedRegion && !isSameCluster(detectedRegion, currentRegionCode)) {
          const regionNames: Record<string, string> = {
            ID: 'Indonesia (ID)',
            SG: 'Singapore / Malaysia (SG/MY)',
            MY: 'Singapore / Malaysia (SG/MY)',
            LK: 'Singapore / Malaysia (SG/MY)',
            TW: 'Taiwan (TW)',
            VN: 'Vietnam (VN)',
            TH: 'Thailand (TH)',
            PH: 'Philippines (PH)',
            LATAM: 'Latin America (LATAM)',
            BR: 'Brazil (BR)',
            GLOBAL: 'Global',
            BD: 'Bangladesh (BD)',
          };

          const regionSlugMap: Record<string, string> = {
            ID: 'indonesia',
            SG: 'my-sg',
            MY: 'my-sg',
            LK: 'my-sg',
            TW: 'tw',
            VN: 'vn',
            TH: 'th',
            PH: 'ph',
            LATAM: 'latam',
            BR: 'br',
            GLOBAL: 'global',
            BD: 'bd',
          };

          const detectedName = regionNames[detectedRegion] || detectedRegion;
          const currentName = regionNames[currentRegionCode] || currentRegionCode;
          const targetSlug = regionSlugMap[detectedRegion] || detectedRegion.toLowerCase();

          const targetUrl = isFreeFire
            ? `/games/free-fire?region=${targetSlug}&prefill=${encodeURIComponent(uidToValidate.trim())}#player-verify`
            : product?.catalogGame?.slug
            ? `/games/${product.catalogGame.slug}?region=${targetSlug}`
            : `/games/${resolvedGame.toLowerCase()}?region=${targetSlug}`;

          const actionButtonText = isFreeFire
            ? `Switch to Free Fire ${detectedName} Top Up`
            : `Switch to ${resolvedGame} ${detectedName} Top Up`;

          setMismatchData({
            detectedRegionCode: detectedRegion,
            detectedRegionName: detectedName,
            currentRegionCode: currentRegionCode,
            currentRegionName: currentName,
            targetUrl,
            actionButtonText,
          });
          setShowIdMismatchModal(true);
        }
      }
      const err = e.response?.data?.message || 'Verification failed';
      if (err === 'REGION_UNAVAILABLE') setErrorMsg('Unsupported region.');
      else if (err === 'MICROSERVICE_EXCEPTION')
        setErrorMsg('Service busy. Please try again.');
      else if (
        err === 'PLAYER_NOT_FOUND' ||
        e.response?.data?.message === 'invalid_id'
      )
        setErrorMsg(
          hasZoneInput
            ? `Invalid ${playerLabel} or ${zoneLabel}`
            : `Invalid ${playerLabel}`,
        );
      else setErrorMsg(err);
    } finally {
      setIsValidating(false);
    }
  };

  const handleReset = () => {
    setTempValResult(null);
    setLocalUid('');
    setLocalZoneId('');
    setErrorMsg(null);
    setGlobalUid('');
    setGlobalValRes(null);
    setPurchasedLevels({});
    setChannelLevelUps([]);
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Card */}
      <div
        className="relative bg-white border border-[#DBEDFF] rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200"
        style={{ fontFamily: 'var(--font-sans), sans-serif' }}
      >
        {/* Blue accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-[#5A98D8] to-[#3b75b5]" />

        <div className="p-6 md:p-8">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="mb-6 text-center">
            <div className="w-12 h-12 rounded-full bg-[#EBF4FF] border border-[#DBEDFF] flex items-center justify-center mx-auto mb-3">
              <img
                src="/assets/avatar-testimonial.png"
                className="w-8 h-8 object-contain rounded-full"
                alt="Player"
              />
            </div>
            <h3 className="text-xl font-bold uppercase tracking-wider text-[#1a5b9b] font-display">
              Verify {hasZoneInput ? `${playerLabel} & ${zoneLabel}` : 'Player ID'}
            </h3>
            <p className="text-xs text-slate-500 uppercase tracking-widest mt-1">
              Enter your {playerLabel} {hasZoneInput ? `and ${zoneLabel}` : ''} to proceed
            </p>
          </div>

          {/* ── Verified State ── */}
          {tempValResult ? (
            <div className="space-y-4">
              {/* Player card */}
              <div className="flex items-center justify-between w-full border border-emerald-200 bg-emerald-50 rounded-lg px-4 py-3 shadow-sm animate-in zoom-in duration-300">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-emerald-100 shadow-sm overflow-hidden shrink-0">
                    <img
                      src="/assets/avatar-testimonial.png"
                      className="w-8 h-8 object-contain rounded-full"
                      alt="Player avatar"
                    />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-emerald-900 leading-tight text-sm">
                      {tempValResult.nickname || 'Unknown Player'}
                    </div>
                    <div className="text-[11px] text-emerald-600 tracking-wide font-mono mt-0.5">
                      {playerLabel}: {localUid}
                      {hasZoneInput && (localZoneId || tempValResult.zoneId) && (
                        <span> · {zoneLabel}: {localZoneId || tempValResult.zoneId}</span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleReset}
                  className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 hover:text-emerald-800 transition-colors ml-3 shrink-0"
                >
                  CHANGE
                </button>
              </div>

              <p className="text-[11px] text-center text-emerald-600 font-bold uppercase tracking-widest animate-in fade-in duration-500">
                ✓ Successfully Verified! Proceeding…
              </p>
            </div>
          ) : (
            /* ── Input State ── */
            <div className="space-y-4">
              <div className="space-y-3">
                {/* UID input with saved players dropdown */}
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-bold tracking-wider block mb-1">
                    {playerLabel}
                  </label>
                  <div className="relative" ref={dropdownRef}>
                    <input
                      type="text"
                      placeholder={`Enter ${playerLabel}`}
                      value={localUid}
                      autoFocus
                      onChange={(e) => {
                        setLocalUid(e.target.value);
                        setErrorMsg(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleValidate();
                      }}
                      className="w-full border border-[#DBEDFF] rounded-lg px-4 py-3 text-sm bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#5A98D8] font-mono tracking-widest pr-10"
                    />

                    {/* Saved players trigger */}
                    {relevantSavedPlayers.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowSavedDropdown(!showSavedDropdown)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
                        aria-label="View saved players"
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${showSavedDropdown ? 'rotate-180' : ''}`}
                        />
                      </button>
                    )}

                    {/* Saved players dropdown */}
                    {showSavedDropdown && relevantSavedPlayers.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden text-left p-1.5 z-[60]">
                        <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 mb-1">
                          Recent Players
                        </div>
                        {relevantSavedPlayers.map((player) => (
                          <button
                            key={player.uid}
                            type="button"
                            onClick={() => {
                              setLocalUid(player.uid);
                              setLocalZoneId(player.zoneId || '');
                              setTempValResult(null);
                              setErrorMsg(null);
                              setShowSavedDropdown(false);
                            }}
                            className="w-full flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg transition-colors group"
                          >
                            <div className="flex items-center gap-3">
                              <img
                                src={player.avatar || '/assets/avatar-testimonial.png'}
                                className="w-9 h-9 rounded-full border border-slate-200 object-cover shadow-sm bg-slate-100"
                                alt=""
                              />
                              <div className="text-left">
                                <div className="text-sm font-bold text-slate-800 line-clamp-1 group-hover:text-[#1a5b9b] transition-colors">
                                  {player.nickname}
                                </div>
                                <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                                  {playerLabel}: {player.uid}
                                  {player.zoneId && ` · ${zoneLabel}: ${player.zoneId}`}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Zone ID input (if required for game) */}
                {hasZoneInput && (
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase font-bold tracking-wider block mb-1">
                      {zoneLabel} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder={`Enter ${zoneLabel}`}
                      value={localZoneId}
                      onChange={(e) => {
                        setLocalZoneId(e.target.value);
                        setErrorMsg(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleValidate();
                      }}
                      className="w-full border border-[#DBEDFF] rounded-lg px-4 py-3 text-sm bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#5A98D8] font-mono tracking-widest"
                    />
                  </div>
                )}
              </div>

              {/* Error message */}
              {errorMsg && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600 animate-in slide-in-from-top-1 duration-200">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}

              {/* Verify button */}
              <button
                type="button"
                onClick={() => handleValidate()}
                disabled={
                  isValidating ||
                  !localUid.trim() ||
                  (hasZoneInput && !localZoneId.trim())
                }
                className="w-full flex items-center justify-center gap-2 bg-[#5A98D8] hover:bg-[#4a88c8] text-white text-xs font-bold uppercase tracking-widest py-3 px-4 rounded-lg transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
              >
                {isValidating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying…
                  </>
                ) : (
                  'Verify & Continue'
                )}
              </button>
            </div>
          )}

          {/* Help tip */}
          <div className="mt-5 text-[10px] text-slate-400 font-mono leading-relaxed text-center uppercase tracking-wider">
            Find your {playerLabel} {hasZoneInput ? `and ${zoneLabel} ` : ''}in your in-game profile settings.
            <br />No password required.
          </div>
        </div>
      </div>

      {/* Region Mismatch Modal */}
      <RegionMismatchModal
        isOpen={showIdMismatchModal}
        onClose={() => setShowIdMismatchModal(false)}
        gameName={isFreeFire ? 'Free Fire' : resolvedGame}
        gameSlug={
          isFreeFire
            ? 'free-fire'
            : product?.catalogGame?.slug || product?.slug || 'free-fire'
        }
        playerId={localUid}
        detectedRegionCode={mismatchData?.detectedRegionCode || 'ID'}
        detectedRegionName={mismatchData?.detectedRegionName || 'Indonesia (ID)'}
        currentRegionCode={
          mismatchData?.currentRegionCode ||
          (targetRegion === 'ID' ? 'ID' : 'SG')
        }
        currentRegionName={
          mismatchData?.currentRegionName ||
          (targetRegion === 'ID'
            ? 'Indonesia (ID)'
            : 'Singapore / Malaysia (SG/MY)')
        }
        targetUrl={
          mismatchData?.targetUrl ||
          (isFreeFire
            ? '/games/free-fire?region=indonesia'
            : '/games/free-fire')
        }
        actionButtonText={mismatchData?.actionButtonText}
      />
    </div>
  );
};

export default PlayerVerifyModal;
